import * as React from "react"
import { Text, Box, Button, Link, useToast, VStack, Spinner, Center, Flex } from "@chakra-ui/react"
import { useNavigate, Link as RLink, useSearchParams, useParams } from "react-router-dom"
import { useQuery, useMutation, useQueryClient } from "react-query"
import { useForm } from "react-hook-form"

import { ReactComponent as Logo } from "../assets/images/logo.svg"
import { Form } from "../components/Form"
import { Input } from "../components/Input"
import { fetcher } from "../lib/fetcher"
import { LoginResponse } from "./Login"
import { AUTH_TOKEN, REFRESH_TOKEN } from "../lib/config"
import { useDocumentTitle } from "../lib/hooks/useDocumentTitle"

interface InvitationEntity {
  user: string | null
  email: string
  organization_name: string
  organization_icon: string
}

interface FieldError {
  message: string
  code: string
}

interface RegistrationErrorResponse {
  detail: {
    non_field_errors?: FieldError[]
    email?: FieldError[]
    password?: FieldError[]
    invitation_uuid?: FieldError[]
  }
}

export const Invitation = () => {
  useDocumentTitle("Register")
  const { id } = useParams()
  const client = useQueryClient()
  const { isLoading } = useQuery<InvitationEntity, string>(`invitations/${id}/`)
  const { mutateAsync: register, isLoading: isRegisterLoading } = useMutation<
    undefined | RegistrationErrorResponse,
    any,
    typeof defaultValues
  >((data) => fetcher("registration/", { body: JSON.stringify(data), method: "post" }))
  const { mutateAsync: login, isLoading: isLoginLoading } = useMutation<
    LoginResponse,
    unknown,
    typeof loginValues
  >((data) => fetcher("jwt/login/", { body: JSON.stringify(data), method: "post" }))

  const navigate = useNavigate()
  const searchParams = useSearchParams()[0]

  const loginValues = { email: "", password: "" }

  const defaultValues = {
    first_name: "",
    last_name: "",
    email: searchParams.get("email") || "",
    password: "",
    invitation_uuid: id,
  }

  const form = useForm({ defaultValues })

  const toast = useToast()

  const handleSubmit = async (data: typeof defaultValues) => {
    try {
      form.clearErrors()
      const values = { ...data, invitation_uuid: id }
      const res = await register(values)

      // Successful registration response is undefined
      if (!res) {
        const loginRes = await login({ email: data.email, password: data.password })
        if (loginRes?.user) {
          localStorage.setItem(AUTH_TOKEN, loginRes.token)
          localStorage.setItem(REFRESH_TOKEN, loginRes.refresh_token)
          client.setQueryData("me/", () => loginRes.user)
          navigate("/")
          toast({ description: "Successfully registered!" })
        } else {
          toast({ description: "Invalid details", status: "error" })
        }
      } else if (res.detail.non_field_errors) {
        toast({ description: "Internal server error", status: "error" })
      } else if (res.detail.invitation_uuid) {
        toast({ description: "Invalid invite token", status: "error" })
      } else if (res.detail) {
        Object.keys(res.detail).map((key) =>
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          form.setError(key, {
            message: (res.detail[key as keyof RegistrationErrorResponse["detail"]] as FieldError[])
              .map((error) => error.message)
              .join(", "),
          }),
        )
      }
    } catch (error) {
      toast({ title: "Network error", description: "Please check your internet connection", status: "error" })
    }
  }

  if (isLoading)
    return (
      <Center>
        <Spinner />
      </Center>
    )
  return (
    <VStack spacing={8}>
      <VStack spacing={4}>
        <Box as={Logo} h={50} fill="gray.700" />
        <Text color="gray.500">FLEET MANAGEMENT SYSTEM</Text>
      </VStack>
      <Form {...form} onSubmit={handleSubmit}>
        <Flex justify="flex-start" w="100%" align="center">
          {searchParams.get("organization_icon") && (
            <img
              style={{
                height: "2.5rem",
                width: "2.5rem",
                objectFit: "contain",
                borderRadius: 5,
              }}
              alt={searchParams.get("organization_name") || ""}
              src={searchParams.get("organization_icon") || ""}
            />
          )}
          <Text pl={4} fontSize="1.2rem">
            {searchParams.get("organization_name")}
          </Text>
        </Flex>
        <Input name="first_name" placeholder="First Name" isRequired />
        <Input name="last_name" placeholder="Last Name" isRequired />
        <Input name="email" placeholder="Email" isRequired isReadOnly />
        <Input name="password" type="password" placeholder="Password" isRequired />
        <Button
          width="100%"
          type="submit"
          colorScheme="green"
          isDisabled={isRegisterLoading || isLoginLoading}
          isLoading={isRegisterLoading || isLoginLoading}
        >
          Register
        </Button>
        <Link to="/" as={RLink}>
          Already have an account?
        </Link>
      </Form>
    </VStack>
  )
}
